import { useEffect } from "react";
import { useSelector } from "react-redux";

/**
 * The jackpot plates' values (Cleopatra's Fire): each plate shows its jackpot
 * in money — the server's options.jackpots points (state.game
 * .jackpotMultipliers; base_bet points = 1x) × the current bet — and follows
 * every bet change (GDD: "change reciprocally with every total bet change").
 * Free-rounds aware: during a campaign the plates show the campaign's bet.
 *
 * The text goes into each room's `txt_slot_jackpot_<tier>` slots
 * (GameViewSimpleController.setJackpotValues).
 */
export const JACKPOT_PLATE_TIERS = ["royal", "grand", "major", "midi", "minor", "mini"];

export const jackpotPlateValues = (jackpots, bet, baseBet) => {
  if (!jackpots || !(bet > 0) || !(baseBet > 0)) return null;
  const values = {};
  for (const tier of JACKPOT_PLATE_TIERS) {
    if (jackpots[tier] > 0) values[tier] = (jackpots[tier] / baseBet) * bet;
  }
  return values;
};

/** Keep a room's plates on the current values. */
export const useJackpotPlates = (controllerRef) => {
  const jackpots = useSelector((state) => state.game.jackpotMultipliers);
  const baseBet = useSelector((state) => state.game.baseBet);
  const bet = useSelector((state) => {
    const fr = state.freeRounds;
    return (fr?.status === "active" && fr?.betValue) || state.game.bet;
  });

  useEffect(() => {
    const values = jackpotPlateValues(jackpots, bet, baseBet);
    if (!values) return undefined;
    // The room builds its controller in its own mount effect, which can run
    // after this one — wait a frame and try again until it exists.
    let raf = null;
    let tries = 120; // ~2 s; a room whose spine never loads is left alone
    const apply = () => {
      const controller = controllerRef.current;
      if (controller?.spine) controller.setJackpotValues(values);
      else if (tries-- > 0) raf = requestAnimationFrame(apply);
    };
    apply();
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [controllerRef, jackpots, bet, baseBet]);
};
